// Keep in sync with backend/src/analytics/feature-use-metadata.ts

export const FEATURE_USE_METADATA_KEYS = [
  'screen',
  'nav',
  'viewport',
  'source',
  'tab',
  'item',
  'outcome',
  'surface',
  'filter_key',
  'has_query',
  'tile',
  'from',
  'page',
  'action',
  'count_bucket',
] as const

export type FeatureUseViewport = 'xs' | 'md' | 'lg'

export const FEATURE_USE_MAX_EVENT_NAME_LENGTH = 64
export const FEATURE_USE_MAX_METADATA_STRING_LENGTH = 64
export const FEATURE_USE_EVENT_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/

const VIEWPORT_VALUES = new Set<string>(['xs', 'md', 'lg'])

export function sanitizeFeatureUseEventName(eventName: unknown): string | null {
  if (typeof eventName !== 'string') return null
  const trimmed = eventName.trim()
  if (!trimmed || trimmed.length > FEATURE_USE_MAX_EVENT_NAME_LENGTH) return null
  if (!FEATURE_USE_EVENT_NAME_PATTERN.test(trimmed)) return null
  return trimmed
}

function isAllowedPrimitive(value: unknown): value is string | number | boolean {
  if (typeof value === 'string') {
    return value.length > 0 && value.length <= FEATURE_USE_MAX_METADATA_STRING_LENGTH
  }
  if (typeof value === 'number') {
    return Number.isFinite(value)
  }
  return typeof value === 'boolean'
}

/** MUI default breakpoints: sm 600, md 900 */
export function resolveFeatureUseViewport(width: number): FeatureUseViewport {
  if (width < 600) return 'xs'
  if (width < 900) return 'md'
  return 'lg'
}

export function stripFeatureUseProps(
  props?: Record<string, unknown>,
): Record<string, string | number | boolean> | undefined {
  if (!props || typeof props !== 'object' || Array.isArray(props)) return undefined

  const out: Record<string, string | number | boolean> = {}

  for (const key of FEATURE_USE_METADATA_KEYS) {
    if (!(key in props)) continue
    const value = props[key]
    if (value === undefined || value === null) continue

    if (key === 'viewport') {
      if (typeof value === 'string' && VIEWPORT_VALUES.has(value)) {
        out.viewport = value
      }
      continue
    }

    if (isAllowedPrimitive(value)) {
      out[key] = value
    }
  }

  return Object.keys(out).length > 0 ? out : undefined
}
